'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import type * as PDFJSLib from 'pdfjs-dist';

// We'll dynamically import pdfjs-dist to avoid SSR issues
let pdfjsLib: typeof PDFJSLib | null = null;

export default function PDFViewer() {
  const router = useRouter();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfDoc, setPdfDoc] = useState<PDFJSLib.PDFDocumentProxy | null>(null);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState(1.5);

  const handleLogout = async () => {
    try {
      await fetch('/api/auth', {
        method: 'DELETE',
      });
      router.push('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    // Disable right-click globally
    const disableContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    // Disable keyboard shortcuts for saving/printing
    const disableKeyboard = (e: KeyboardEvent) => {
      // Ctrl/Cmd + S (Save)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        return false;
      }
      // Ctrl/Cmd + P (Print)
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', disableContextMenu);
    document.addEventListener('keydown', disableKeyboard);

    return () => {
      document.removeEventListener('contextmenu', disableContextMenu);
      document.removeEventListener('keydown', disableKeyboard);
    };
  }, []);

  // Load PDF
  useEffect(() => {
    const loadPdf = async () => {
      try {
        setLoading(true);
        console.log('Starting PDF load...');

        // Dynamically import pdfjs-dist only on client side
        if (!pdfjsLib) {
          console.log('Loading pdfjs-dist...');
          pdfjsLib = await import('pdfjs-dist');

          // Configure PDF.js worker using local file in public folder
          pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
          console.log('pdfjs-dist loaded, version:', pdfjsLib.version);
        }

        // Fetch PDF from our authenticated API
        const response = await fetch('/api/pdf');
        console.log('PDF fetch response:', response.status);

        if (!response.ok) {
          throw new Error('Failed to load PDF');
        }

        const arrayBuffer = await response.arrayBuffer();
        console.log('PDF arrayBuffer size:', arrayBuffer.byteLength);

        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;
        console.log('PDF loaded successfully, pages:', pdf.numPages);

        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setLoading(false);
      } catch (error) {
        console.error('Error loading PDF:', error);
        setLoading(false);
      }
    };

    loadPdf();
  }, []);

  // Render page
  useEffect(() => {
    const renderPage = async () => {
      if (!pdfDoc || !canvasRef.current) {
        console.log('Render skipped - pdfDoc:', !!pdfDoc, 'canvas:', !!canvasRef.current);
        return;
      }

      try {
        console.log('Rendering page', pageNumber, 'at scale', scale);

        const page = await pdfDoc.getPage(pageNumber);
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        if (!context) {
          console.error('Failed to get canvas context');
          return;
        }

        // Use device pixel ratio for sharper rendering on high-DPI displays
        const devicePixelRatio = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: scale * devicePixelRatio });
        console.log('Viewport dimensions:', viewport.width, 'x', viewport.height);

        // Set canvas dimensions at device resolution
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // Scale canvas display size to match intended size
        canvas.style.width = `${viewport.width / devicePixelRatio}px`;
        canvas.style.height = `${viewport.height / devicePixelRatio}px`;

        // Render PDF page
        const renderContext = {
          canvasContext: context,
          canvas: canvas,
          viewport: viewport,
        };

        await page.render(renderContext).promise;
        console.log('Page rendered successfully');
      } catch (error) {
        console.error('Error rendering page:', error);
      }
    };

    renderPage();
  }, [pdfDoc, pageNumber, scale]);

  // Set initial scale based on container width (only once on mount)
  useEffect(() => {
    if (containerRef.current && !pdfDoc) {
      const containerWidth = containerRef.current.offsetWidth;
      // Set initial scale based on screen width
      if (containerWidth < 640) {
        setScale(0.8); // Mobile
      } else if (containerWidth < 1024) {
        setScale(1.2); // Tablet
      } else {
        setScale(1.5); // Desktop
      }
    }
  }, [pdfDoc]);

  const goToPrevPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const goToNextPage = () => {
    if (pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 3));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <div className="bg-white shadow-md px-4 py-4 md:py-5 flex justify-between items-center border-b-2" style={{ borderBottomColor: '#2380E0' }}>
        <div className="flex items-center gap-3">
          <img
            src="/logos/logo_ok.png"
            alt="MTSolutions"
            style={{ margin: 0, padding: 0, height: 'auto', maxHeight: '45px', width: 'auto', objectFit: 'contain' }}
          />
          <h1 className="text-lg md:text-xl font-bold" style={{ color: '#434448' }}>
            PDF Viewer
          </h1>
        </div>
        <button
          onClick={handleLogout}
          className="text-white px-4 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
          style={{ backgroundColor: '#FC8021' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#e3721d';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#FC8021';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Logout
        </button>
      </div>

      {/* PDF Viewer */}
      <div className="container mx-auto py-4 px-2 md:py-8 md:px-4">
        <div className="bg-white rounded-xl shadow-xl p-3 md:p-6 border border-gray-200">
          <div className="mb-4 text-sm" style={{ color: '#434448' }}>
            <p className="font-medium">Viewing: plano.pdf</p>
            <p className="text-xs mt-1 opacity-70">Protected document - download and print disabled</p>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4 pb-4 border-b-2" style={{ borderBottomColor: '#2380E0' }}>
            <div className="flex items-center gap-2">
              <button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1 || loading}
                className="text-white px-4 py-2 text-sm rounded-lg disabled:cursor-not-allowed font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                style={{
                  backgroundColor: pageNumber <= 1 || loading ? '#d1d5db' : '#2380E0',
                }}
                onMouseEnter={(e) => {
                  if (!(pageNumber <= 1 || loading)) {
                    e.currentTarget.style.backgroundColor = '#1c6bc4';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!(pageNumber <= 1 || loading)) {
                    e.currentTarget.style.backgroundColor = '#2380E0';
                  }
                }}
              >
                Prev
              </button>
              <span className="text-sm whitespace-nowrap font-medium" style={{ color: '#434448' }}>
                Page {pageNumber} of {numPages || '...'}
              </span>
              <button
                onClick={goToNextPage}
                disabled={pageNumber >= numPages || loading}
                className="text-white px-4 py-2 text-sm rounded-lg disabled:cursor-not-allowed font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                style={{
                  backgroundColor: pageNumber >= numPages || loading ? '#d1d5db' : '#2380E0',
                }}
                onMouseEnter={(e) => {
                  if (!(pageNumber >= numPages || loading)) {
                    e.currentTarget.style.backgroundColor = '#1c6bc4';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!(pageNumber >= numPages || loading)) {
                    e.currentTarget.style.backgroundColor = '#2380E0';
                  }
                }}
              >
                Next
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={zoomOut}
                disabled={loading}
                className="text-white px-4 py-2 text-sm rounded-lg disabled:bg-gray-400 font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                style={{
                  backgroundColor: loading ? '#d1d5db' : '#434448',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = '#2d2e31';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = '#434448';
                  }
                }}
              >
                -
              </button>
              <span className="text-sm font-medium" style={{ color: '#434448' }}>{Math.round(scale * 100)}%</span>
              <button
                onClick={zoomIn}
                disabled={loading}
                className="text-white px-4 py-2 text-sm rounded-lg disabled:bg-gray-400 font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                style={{
                  backgroundColor: loading ? '#d1d5db' : '#434448',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = '#2d2e31';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = '#434448';
                  }
                }}
              >
                +
              </button>
            </div>
          </div>

          {/* PDF Canvas */}
          <div
            ref={containerRef}
            className="relative overflow-auto max-h-[calc(100vh-280px)] rounded-lg"
            style={{
              backgroundColor: '#e5e7eb',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              WebkitTouchCallout: 'none',
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              return false;
            }}
            onMouseDown={(e) => {
              if (e.button === 2 || e.button === 1) {
                e.preventDefault();
                return false;
              }
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-gray-600">Loading PDF...</div>
              </div>
            ) : (
              <div className="flex justify-center p-4">
                <canvas
                  ref={canvasRef}
                  className="shadow-lg"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Watermark overlay */}
      <div
        className="fixed inset-0 pointer-events-none flex items-center justify-center"
        style={{
          background: 'transparent',
          zIndex: 9999,
        }}
      >
        <div
          className="opacity-10 transform rotate-45 text-4xl md:text-6xl font-bold"
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            color: '#2380E0',
          }}
        >
          PROTECTED
        </div>
      </div>
    </div>
  );
}
